import React, { useEffect, useState } from "react";
import { ChevronDown, ChevronUp, X } from "lucide-react";

type Meridiem = "AM" | "PM";

interface TimeDraft {
    timeValue: string;
    meridiem: Meridiem;
}

interface TimePickerModalProps {
    open: boolean;
    value: string;
    label: string;
    onApply: (value: string) => void;
    onClose: () => void;
    title?: string;
    description?: string;
    minTime?: string;
    maxTime?: string;
}

const toPaddedNumber = (value: number) => value.toString().padStart(2, "0");
const keepNumericText = (value: string) => value.replace(/\D/g, "").slice(0, 2);

const parseDraftTimeParts = (value: string) => {
    const [hourPart = "", minutePart = ""] = value.split(":");

    return {
        hour: hourPart,
        minute: minutePart,
    };
};

const normalizeHourValue = (value: string) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 1) {
        return 1;
    }

    if (parsed > 12) {
        return 12;
    }

    return parsed;
};

const normalizeMinuteValue = (value: string) => {
    const parsed = Number.parseInt(value, 10);

    if (Number.isNaN(parsed) || parsed < 0) {
        return 0;
    }

    if (parsed > 59) {
        return 59;
    }

    return parsed;
};

const createDraftFromTime = (value: string): TimeDraft => {
    if (!value) {
        return {
            timeValue: "9:00",
            meridiem: "AM",
        };
    }

    const [hoursString, minutesString] = value.split(":");
    const hours = Number(hoursString);
    const minutes = Number(minutesString);
    const meridiem: Meridiem = hours >= 12 ? "PM" : "AM";
    const hour = hours % 12 === 0 ? 12 : hours % 12;

    return {
        timeValue: `${hour}:${toPaddedNumber(Number.isNaN(minutes) ? 0 : minutes)}`,
        meridiem,
    };
};

const createTimeString = ({ timeValue, meridiem }: TimeDraft) => {
    const parts = parseDraftTimeParts(timeValue);
    const normalizedHour = normalizeHourValue(parts.hour);
    const normalizedMinute = normalizeMinuteValue(parts.minute);
    let hours24 = normalizedHour % 12;

    if (meridiem === "PM") {
        hours24 += 12;
    }

    return `${toPaddedNumber(hours24)}:${toPaddedNumber(normalizedMinute)}`;
};

const formatDraftTimeValue = ({ timeValue, meridiem }: TimeDraft) => {
    const parts = parseDraftTimeParts(timeValue);
    const normalizedHour = normalizeHourValue(parts.hour);
    const normalizedMinute = normalizeMinuteValue(parts.minute);

    return `${normalizedHour}:${toPaddedNumber(normalizedMinute)} ${meridiem}`;
};

const normalizeDraftTimeValue = (value: string) => {
    const parts = parseDraftTimeParts(value);
    const normalizedHour = normalizeHourValue(parts.hour);
    const normalizedMinute = normalizeMinuteValue(parts.minute);

    return `${normalizedHour}:${toPaddedNumber(normalizedMinute)}`;
};

const formatTimeLimitLabel = (value: string) => {
    const [hoursString, minutesString] = value.split(":");
    const hours = Number(hoursString);
    const minutes = Number(minutesString);
    const meridiem = hours >= 12 ? "PM" : "AM";
    const displayHour = hours % 12 === 0 ? 12 : hours % 12;

    return `${displayHour}:${toPaddedNumber(minutes)} ${meridiem}`;
};

export default function TimePickerModal({
    open,
    value,
    label,
    onApply,
    onClose,
    title = "Choose a time",
    description = "Set the exact time for this request.",
    minTime,
    maxTime,
}: TimePickerModalProps) {
    const [timeDraft, setTimeDraft] = useState<TimeDraft>(() => createDraftFromTime(value));
    const draftTimeParts = parseDraftTimeParts(timeDraft.timeValue);
    const selectedDraftHour = normalizeHourValue(draftTimeParts.hour);
    const selectedDraftMinute = normalizeMinuteValue(draftTimeParts.minute);
    const normalizedDraft = {
        ...timeDraft,
        timeValue: normalizeDraftTimeValue(timeDraft.timeValue),
    };
    const draftTimeString = createTimeString(normalizedDraft);
    const isBeforeMinTime = Boolean(minTime && draftTimeString < minTime);
    const isAfterMaxTime = Boolean(maxTime && draftTimeString > maxTime);
    const validationMessage = isBeforeMinTime
        ? `Earliest allowed time is ${formatTimeLimitLabel(minTime!)}`
        : isAfterMaxTime
          ? `Latest allowed time is ${formatTimeLimitLabel(maxTime!)}`
          : "";

    useEffect(() => {
        if (!open) {
            return;
        }

        setTimeDraft(createDraftFromTime(value));
    }, [open, value]);

    useEffect(() => {
        if (!open || typeof document === "undefined") {
            return;
        }

        const { overflow } = document.body.style;
        document.body.style.overflow = "hidden";

        return () => {
            document.body.style.overflow = overflow;
        };
    }, [open]);

    if (!open) {
        return null;
    }

    const updateDraftHourInput = (nextValue: string) => {
        const cleanedValue = keepNumericText(nextValue);

        setTimeDraft((prev) => ({
            ...prev,
            timeValue: `${cleanedValue}:${toPaddedNumber(selectedDraftMinute)}`,
        }));
    };

    const updateDraftHour = (hourValue: string | number) => {
        const normalizedHour = normalizeHourValue(String(hourValue));

        setTimeDraft((prev) => ({
            ...prev,
            timeValue: `${normalizedHour}:${toPaddedNumber(selectedDraftMinute)}`,
        }));
    };

    const updateDraftMinute = (minuteValue: string) => {
        const normalizedMinute = normalizeMinuteValue(minuteValue);

        setTimeDraft((prev) => ({
            ...prev,
            timeValue: `${selectedDraftHour}:${toPaddedNumber(normalizedMinute)}`,
        }));
    };

    const stepDraftMinute = (delta: number) => {
        updateDraftMinute(String((selectedDraftMinute + delta + 60) % 60));
    };

    const stepDraftHour = (delta: number) => {
        updateDraftHour(((selectedDraftHour - 1 + delta + 12) % 12) + 1);
    };

    const handleApply = () => {
        if (validationMessage) {
            return;
        }

        onApply(draftTimeString);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <button
                type="button"
                aria-label="Close picker"
                className="absolute inset-0 bg-slate-950/45 backdrop-blur-[1px]"
                onClick={onClose}
            />

            <div
                role="dialog"
                aria-modal="true"
                className="relative w-full max-w-[22rem] rounded-[1.75rem] bg-white px-4 pb-4 pt-4 shadow-[0_28px_90px_rgba(15,23,42,0.18)]"
            >
                <div className="mb-4 flex items-start justify-between gap-4">
                    <div>
                        <p className="text-sm font-semibold text-slate-900">{title}</p>
                        <p className="mt-1 text-sm text-slate-900">{description}</p>
                    </div>

                    <button
                        type="button"
                        onClick={onClose}
                        className="rounded-full border border-slate-200 p-2 text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
                    >
                        <X size={18} />
                    </button>
                </div>

                <div className="space-y-3">
                    <div className="mx-auto max-w-[18rem] rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                        <p className="text-[10px] uppercase tracking-[0.12em] text-slate-400">{label}</p>
                        <p className="mt-0.5 text-[15px] font-semibold text-slate-900">
                            {formatDraftTimeValue(timeDraft)}
                        </p>
                        {validationMessage ? (
                            <p className="mt-1 text-xs font-medium text-red-500">{validationMessage}</p>
                        ) : null}
                    </div>

                    <div className="mx-auto max-w-[18rem] overflow-hidden rounded-xl border border-slate-200 bg-white">
                        <div className="grid grid-cols-[72px_10px_72px_72px] justify-center gap-2 px-3 py-3">
                            <div className="space-y-1">
                                <p className="text-[10px] font-medium text-slate-900">Hours</p>
                                <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                                    <div className="bg-blue-600 px-2 py-2">
                                        <input
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={toPaddedNumber(selectedDraftHour)}
                                            onChange={(event) => updateDraftHourInput(event.target.value)}
                                            className="w-full border-0 bg-transparent p-0 text-center text-base font-semibold tracking-[0.04em] text-white outline-none"
                                        />
                                    </div>
                                    <div className="grid grid-rows-2 bg-white">
                                        <button
                                            type="button"
                                            onClick={() => stepDraftHour(1)}
                                            className="flex h-6 items-center justify-center border-b border-slate-200 text-slate-600 transition-colors hover:bg-slate-50"
                                            aria-label="Increase hour"
                                        >
                                            <ChevronUp size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => stepDraftHour(-1)}
                                            className="flex h-6 items-center justify-center text-slate-600 transition-colors hover:bg-slate-50"
                                            aria-label="Decrease hour"
                                        >
                                            <ChevronDown size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="pt-8 text-xl font-light text-slate-500">-</div>

                            <div className="space-y-1">
                                <p className="text-[10px] font-medium text-slate-900">Minutes</p>
                                <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                                    <div className="bg-blue-600 px-2 py-2">
                                        <input
                                            inputMode="numeric"
                                            maxLength={2}
                                            value={toPaddedNumber(selectedDraftMinute)}
                                            onChange={(event) => updateDraftMinute(event.target.value)}
                                            className="w-full border-0 bg-transparent p-0 text-center text-base font-semibold tracking-[0.04em] text-white outline-none"
                                        />
                                    </div>
                                    <div className="grid grid-rows-2 bg-white">
                                        <button
                                            type="button"
                                            onClick={() => stepDraftMinute(1)}
                                            className="flex h-6 items-center justify-center border-b border-slate-200 text-slate-600 transition-colors hover:bg-slate-50"
                                            aria-label="Increase minute"
                                        >
                                            <ChevronUp size={14} />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => stepDraftMinute(-1)}
                                            className="flex h-6 items-center justify-center text-slate-600 transition-colors hover:bg-slate-50"
                                            aria-label="Decrease minute"
                                        >
                                            <ChevronDown size={14} />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-1">
                                <p className="text-[10px] font-medium text-slate-900">AM/PM</p>
                                <div className="overflow-hidden rounded-lg border border-slate-200 shadow-sm">
                                    {(["AM", "PM"] as Meridiem[]).map((option, index) => (
                                        <button
                                            key={option}
                                            type="button"
                                            onClick={() =>
                                                setTimeDraft((prev) => ({
                                                    ...prev,
                                                    meridiem: option,
                                                }))
                                            }
                                            className={`flex h-[36px] w-full items-center justify-center text-sm font-semibold transition-colors ${
                                                timeDraft.meridiem === option
                                                    ? "bg-blue-600 text-white"
                                                    : "bg-white text-slate-700 hover:bg-slate-50"
                                            } ${index === 0 ? "border-b border-slate-200" : ""}`}
                                        >
                                            {option}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 border-t border-slate-200 bg-slate-50 px-3 py-2.5">
                            <button
                                type="button"
                                onClick={handleApply}
                                disabled={Boolean(validationMessage)}
                                className="rounded-lg bg-blue-600 px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300"
                            >
                                OK
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-xs font-medium text-slate-800 transition-colors hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
